import React, { useEffect } from 'react'
import ScreenWrapper from '~/components/screen_wrappers/screen_wrapper'
import {
  getAgency as getAgencySelector
} from '@verzekeringapp-mono/shared/src/state/selectors/agency'
import { useAgency } from '@verzekeringapp-mono/shared/src/hooks/use_agency'
import { useAppSelector } from '@verzekeringapp-mono/shared/src/hooks/state'
import ContactInfoButton from './components/contact_info_button'
import { GoogleMap, MarkerF, useJsApiLoader } from '@react-google-maps/api'
import useGeocode from '@verzekeringapp-mono/shared/src/hooks/use_geocode'
import { NH_LAT_LNG } from '@verzekeringapp-mono/shared/src/state/constants/constants'
import SETTINGS from '@verzekeringapp-mono/shared/src/constants/settings'
import { useEmergencyModalContext } from '~/providers/EmergencyModalProvider'
import { parse } from 'date-fns'
import { renderOpeningHoursText } from '@verzekeringapp-mono/shared/src/utils/agency_utils'
import { getResourceAuthorization } from '@verzekeringapp-mono/shared/src/state/selectors/resource_authorization'

const AdvisorScreen: React.FC = () => {
  const currentResourceAuthorization = useAppSelector(getResourceAuthorization)
  const { getAgency } = useAgency()
  const { open } = useEmergencyModalContext()
  const agency = useAppSelector(getAgencySelector)

  const [center, setCenter] = React.useState(NH_LAT_LNG)

  const { geocodeAsyncGoogleMaps } = useGeocode()

  useEffect(() => {
    const agencyId = currentResourceAuthorization?.relationInfo?.agentNumber

    if (agencyId) {
      getAgency(agencyId)
    }
  }, [])

  useEffect(() => {
    if (agency) {
      geocodeAsyncGoogleMaps(
        [agency?.street, agency.houseNumber, agency.city]
          .filter(Boolean)
          .join(' ')
      ).then(result => {
        if (result) {
          setCenter({
            lat: result.latitude,
            lng: result.longitude
          })
        }
      })
    }
  }, [agency])

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? ''
  })

  if (!agency) {
    return (
      <ScreenWrapper hasPadding={false}>
        <div className='rounded bg-surface-card p-8'>
          <p className='text-center text-sm text-text-muted'>
            Er is iets misgegaan bij het ophalen van de gegevens van de
            adviseur. Probeer het later opnieuw.
          </p>
        </div>
      </ScreenWrapper>
    )
  }

  const currentDay = new Date().getDay()
  const currentDayIsOpen = agency.openingHours.find(
    openingHour => new Date().getDay() === openingHour.dayNumber
  )

  const currentHour = new Date().getHours()
  const currentHourIsOpen = agency?.openingHours.find(
    openingHour =>
      new Date().getDay() === openingHour.dayNumber &&
      openingHour.opening != null &&
      openingHour.closing != null &&
      parse(openingHour.opening, 'HH:mm', new Date()).getHours() <=
        currentHour &&
      parse(openingHour.closing, 'HH:mm', new Date()).getHours() >
        currentHour &&
      openingHour.closed === false
  )

  return (
    <ScreenWrapper hasPadding={false}>
      <div className='rounded bg-surface-card p-8'>
        <div className='md:hidden w-fits flex justify-center h-fit p-4'>
          <img
            src={`${currentResourceAuthorization?.relationInfo?.logo}?${Date.now()}`}
            alt='Logo'
            className=' w-20 h-20 object-contain'
          />
        </div>

        <p className='text-center text-sm text-text-muted md:hidden'>
          {renderOpeningHoursText(agency.openingHours)}
        </p>

        <div className='flex items-center'>
          <div className='grid md:grid-cols-2 md:gap-8 mt-8 grid-cols-1'>
            <div className='flex flex-col-reverse md:flex-col'>
              <div>
                <p className='text-xl mb-4'>{agency.name}</p>

                <p className='mb-2 font-semibold'>Adres</p>

                <p>{`${agency?.street} ${agency?.houseNumber}${agency?.houseNumberAddition ? agency?.houseNumberAddition : ''}`}</p>

                <p className='mb-6'>{`${agency?.zipCode} ${agency?.city}`}</p>
              </div>

              <div className='flex flex-col md:block'>
                <p className='mb-2 hidden md:block font-semibold'>Contact</p>

                <div className='mt-1 flex justify-center flex-row gap-8 md:gap-0 md:flex-col'>
                  <ContactInfoButton
                    description={agency?.phoneNumber}
                    icon='phone'
                    title={'Bellen'}
                    href={`tel:${agency?.phoneNumber}`}
                  />

                  <ContactInfoButton
                    description={agency?.email}
                    icon='envelope'
                    title={'Bericht'}
                    href={`mailto:${agency?.email}`}
                  />

                  <ContactInfoButton
                    description={agency?.whatsappNumber}
                    icon={['fab', 'whatsapp']}
                    title={'WhatsApp'}
                    variant='whatsapp'
                    href={`https://wa.me/${agency?.whatsappNumber}`}
                  />

                  <ContactInfoButton
                    icon={['fas', 'globe']}
                    title={'Website'}
                    description={agency.website}
                    href={
                      agency?.website
                        ? agency.website.startsWith('http')
                          ? agency.website
                          : `https://${agency.website}`
                        : undefined
                    }
                  />

                  <ContactInfoButton
                    description={SETTINGS.emergencyPhoneNumber}
                    icon='phone-plus'
                    title={'Nood'}
                    variant='danger'
                    onClick={() => {
                      open()
                    }}
                  />
                </div>
              </div>
            </div>

            <div>
              <p className='mb-2 font-semibold'>Openingstijden</p>

              {agency?.openingHours.map(openingHour => (
                <div
                  className='flex justify-between mb-2 flex-row'
                  key={openingHour.day}
                >
                  <p
                    className={`${
                      currentDay === openingHour.dayNumber
                        ? currentDayIsOpen && currentHourIsOpen
                          ? 'text-status-success-foreground'
                          : 'text-status-danger-foreground'
                        : ''
                    }`}
                  >
                    {openingHour.day}
                  </p>

                  <p
                    className={`${
                      currentDay === openingHour.dayNumber
                        ? currentDayIsOpen && currentHourIsOpen
                          ? 'text-status-success-foreground'
                          : 'text-status-danger-foreground'
                        : ''
                    }`}
                  >
                    {openingHour.closed === true
                      ? 'gesloten'
                      : `${openingHour.opening} tot ${openingHour.closing}`}
                  </p>
                </div>
              ))}

              <p className='paragraph-light'>
                Buiten kantooruren kun je in gevallen van nood bellen met de
                alarmcentrale van Nh1816 op <br />

                <button
                  type='button'
                  onClick={open}
                  className='text-status-danger-foreground'
                >
                  {SETTINGS.emergencyPhoneNumber}
                </button>
              </p>
            </div>
          </div>
        </div>

        <div className='mt-8 flex h-[300px] w-full items-center justify-center bg-status-success-subtle'>
          {isLoaded
            ? (
              <GoogleMap
                id='google-map-advisory-screen'
                mapContainerClassName='w-full h-full rounded-lg'
                options={{
                  fullscreenControl: false,
                  mapTypeControl: false
                }}
                center={center}
                zoom={10}
              >
                <MarkerF position={center} />
              </GoogleMap>
            )
            : (
              <p>Loading...</p>
            )}
        </div>
      </div>
    </ScreenWrapper>
  )
}

export default AdvisorScreen
